import { Pencil, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { AccountantForm } from '@/components/accountant/AccountantForm';
import { HistoryList } from '@/components/accountant/HistoryList';
import { ReadonlyLinkCard } from '@/components/accountant/ReadonlyLinkCard';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import type { AccountantDTO } from '@/features/accountant/api';
import {
  useAccountantMutations,
  useAccountantShares,
  useAccountants,
} from '@/features/accountant/hooks';
import { SHARE_MODE_LABEL, type AccountantFormInput } from '@/features/accountant/schemas';
import { ApiError } from '@/lib/api';

export default function AccountantPage() {
  const query = useAccountants();
  const mutations = useAccountantMutations();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AccountantDTO | null>(null);
  const [removing, setRemoving] = useState<AccountantDTO | null>(null);

  const onError = (err: unknown): void => {
    const msg = err instanceof ApiError ? err.message : 'Falha ao concluir a ação';
    toast.show({ title: msg, variant: 'error' });
  };

  const handleCreate = async (values: AccountantFormInput): Promise<void> => {
    try {
      await mutations.create.mutateAsync(values);
      toast.show({ title: 'Contador cadastrado.', variant: 'success' });
      setCreating(false);
    } catch (err) {
      onError(err);
    }
  };

  const handleUpdate = async (
    id: string,
    values: AccountantFormInput,
  ): Promise<void> => {
    try {
      await mutations.update.mutateAsync({ id, payload: values });
      toast.show({ title: 'Contador atualizado.', variant: 'success' });
      setEditing(null);
    } catch (err) {
      onError(err);
    }
  };

  const handleRemove = async (id: string): Promise<void> => {
    try {
      await mutations.remove.mutateAsync(id);
      toast.show({ title: 'Contador removido.', variant: 'success' });
      setRemoving(null);
    } catch (err) {
      onError(err);
    }
  };

  const accountants = query.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Contador</h1>
          <p className="text-sm text-(--color-fg-muted)">
            Compartilhamento mensal com seu contador: pacote automático ou link read-only.
          </p>
        </div>
        <Button type="button" onClick={() => setCreating(true)}>
          <UserPlus className="mr-1 h-4 w-4" aria-hidden /> Novo contador
        </Button>
      </header>

      {query.isError ? (
        <p
          role="alert"
          className="rounded-md border border-(--color-error) p-3 text-sm text-(--color-error)"
        >
          Não foi possível carregar os contadores.
        </p>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando…</p>
      ) : accountants.length === 0 ? (
        <Card>
          <p className="text-sm text-(--color-fg-muted)">
            Nenhum contador cadastrado. Adicione um para compartilhar o fechamento mensal.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          {accountants.map((a) => (
            <AccountantBlock
              key={a.id}
              accountant={a}
              onEdit={() => setEditing(a)}
              onRemove={() => setRemoving(a)}
              onGenerate={async () => {
                try {
                  await mutations.generateLink.mutateAsync(a.id);
                } catch (err) {
                  onError(err);
                }
              }}
              onRotate={async () => {
                try {
                  await mutations.rotateToken.mutateAsync(a.id);
                } catch (err) {
                  onError(err);
                }
              }}
              isGenerating={mutations.generateLink.isPending}
              isRotating={mutations.rotateToken.isPending}
            />
          ))}
        </div>
      )}

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo contador</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <AccountantForm
              submitLabel="Cadastrar"
              isSubmitting={mutations.create.isPending}
              onSubmit={handleCreate}
              onCancel={() => setCreating(false)}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar contador</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {editing ? (
              <AccountantForm
                submitLabel="Salvar"
                initial={{
                  name: editing.name,
                  email: editing.email,
                  shareMode: editing.shareMode,
                  monthlyPackageDay: editing.monthlyPackageDay,
                }}
                isSubmitting={mutations.update.isPending}
                onSubmit={(values) => handleUpdate(editing.id, values)}
                onCancel={() => setEditing(null)}
              />
            ) : null}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover contador</DialogTitle>
            <p className="text-sm text-(--color-fg-muted)">
              {removing?.name} — {removing?.email}. Os envios automáticos serão interrompidos.
            </p>
          </DialogHeader>
          <DialogBody />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setRemoving(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={mutations.remove.isPending}
              onClick={() => removing && handleRemove(removing.id)}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccountantBlock({
  accountant,
  onEdit,
  onRemove,
  onGenerate,
  onRotate,
  isGenerating,
  isRotating,
}: {
  accountant: AccountantDTO;
  onEdit: () => void;
  onRemove: () => void;
  onGenerate: () => Promise<void>;
  onRotate: () => Promise<void>;
  isGenerating: boolean;
  isRotating: boolean;
}) {
  const shares = useAccountantShares(accountant.id);
  const canLink =
    accountant.shareMode === 'READONLY_LINK' || accountant.shareMode === 'BOTH';

  return (
    <section className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">{accountant.name}</h2>
            <p className="text-sm text-(--color-fg-muted)">{accountant.email}</p>
            <p className="mt-1 text-xs text-(--color-fg-muted)">
              {SHARE_MODE_LABEL[accountant.shareMode]} · envio dia{' '}
              {accountant.monthlyPackageDay}
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onEdit} aria-label="Editar">
              <Pencil className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              aria-label="Remover"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      </Card>

      {canLink ? (
        <ReadonlyLinkCard
          accountant={accountant}
          onGenerate={onGenerate}
          onRotate={onRotate}
          isGenerating={isGenerating}
          isRotating={isRotating}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Histórico</CardTitle>
        </CardHeader>
        <HistoryList shares={shares.data ?? []} isLoading={shares.isLoading} />
      </Card>
    </section>
  );
}
